import React, { useState, useEffect, useRef } from 'react';

// Add this utility to return the appropriate shape class for an octave
const getFigureShapeClass = (octave, colorMode) => {
  if (colorMode !== 'figure') return '';
  switch (octave) {
    case 1: return 'figure-x';
    case 2: return 'figure-square';
    case 3: return 'figure-circle';
    case 4: return 'figure-triangle';
    default: return ''; // No star shape used
  }
};



const DigitalInstrumentarium = () => {
  const lastTouchTimeRef = useRef(0);
  // State variables
  const [instrumentType, setInstrumentType] = useState('xylophone');
  const [instrumentRange, setInstrumentRange] = useState('alto');
  const [colorMode, setColorMode] = useState('default');
  const [playingNotes, setPlayingNotes] = useState([]); // Changed to array for multi-note support
  const [removedNotes, setRemovedNotes] = useState([]);
  const [touchInfo, setTouchInfo] = useState({ isActive: false, touchedBarIds: [] }); // Modified to support multiple bars
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [useFlats, setUseFlats] = useState(false);  // For B♭
  const [useSharps, setUseSharps] = useState(false); // For F♯
  const [setupMenuOpen, setSetupMenuOpen] = useState(false); // For setup menu toggle
  const [infoMenuOpen, setInfoMenuOpen] = useState(false); // For instructions toggle
  const [recentlyTouched, setRecentlyTouched] = useState({}); // Changed to object for multiple notes
  
  // Audio context
  const audioContextRef = useRef(null);
  
  // Generate notes based on instrument range
  const generateNotes = () => {
    let notes = [];
    
    // Base octave for different ranges
    let baseOctave = 2; // Default for alto
    if (instrumentRange === 'soprano') baseOctave = 3;
    if (instrumentRange === 'bass') baseOctave = 1;
    
    // Block glockenspiel bass range
    if (instrumentType === 'glockenspiel' && instrumentRange === 'bass') {
      setInstrumentRange('alto');
      baseOctave = 2;
    }
    
    // Generate C-A for first octave
    for (const note of ['C', 'D', 'E', 'F', 'G', 'A']) {
      notes.push({ note, octave: baseOctave });
    }
    
    // Add B from first octave
    notes.push({ note: 'B', octave: baseOctave });
    
    // Generate C-A for second octave
    for (const note of ['C', 'D', 'E', 'F', 'G', 'A']) {
      notes.push({ note, octave: baseOctave + 1 });
    }
    
    return notes;
  };
  
  const [activeNotes, setActiveNotes] = useState(generateNotes());
  
  // Effect for initializing audio context
  useEffect(() => {
    // Initialize audio context on first interaction
    const handleFirstInteraction = () => {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);
    };
    
    document.addEventListener('click', handleFirstInteraction);
    document.addEventListener('touchstart', handleFirstInteraction);
    
    return () => {
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);
    };
  }, []);

  // Effect to prevent context menu (right-click)
  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Add the event listener to the document
    document.addEventListener('contextmenu', handleContextMenu);

    // Return a cleanup function to remove the event listener
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);
  
  // Effect for keyboard handling
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Top row keys on QWERTY keyboard
      const keyMap = {
        '`': 0, '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, 
        '7': 7, '8': 8, '9': 9, '0': 10, '-': 11, '=': 12
      };
      
      if (keyMap[e.key] !== undefined && keyMap[e.key] < activeNotes.length) {
        const note = activeNotes[keyMap[e.key]];
        const noteId = `${note.note}${note.octave}`;
        
        // Only play if the note is not removed
        if (!removedNotes.includes(noteId)) {
          playNote(note.note, note.octave);
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeNotes, removedNotes]);
  
  // Effect for updating notes when range changes
  useEffect(() => {
    setActiveNotes(generateNotes());
    setRemovedNotes([]);
  }, [instrumentRange, instrumentType]);
  
  // Function to get note frequency
  const getNoteFrequency = (note, octave) => {
    // Frequency ratios from the Orff xylophone specifications
    const frequencyRatios = {
      'C': 1.00,
      'D': 9/8,
      'E': 5/4,
      'F': 4/3,
      'F#': 45/32, // F sharp
      'G': 3/2,
      'A': 5/3,
      'Bb': 9/5,   // B flat
      'B': 15/8,
    };
    
    // Handle accidentals
    let adjustedNote = note;
    if (note === 'F' && useSharps) adjustedNote = 'F#';
    if (note === 'B' && useFlats) adjustedNote = 'Bb';
    
    // Base frequency calculation
    let baseFrequency;
    
    // Base octave depends on instrument range
    let baseOctave = 2; // Default for alto
    if (instrumentRange === 'soprano') baseOctave = 3;
    if (instrumentRange === 'bass') baseOctave = 1;
    
    // If we're in the first octave of the range
    if (octave === baseOctave) {
      // Get base C frequency for the instrument range
      const cFreq = 261.63 * Math.pow(2, baseOctave - 4); // C4 = 261.63 Hz
      baseFrequency = cFreq * frequencyRatios[adjustedNote];
    } 
    // If we're in the second octave of the range
    else if (octave === baseOctave + 1) {
      // Get base C frequency for an octave higher
      const cFreq = 261.63 * Math.pow(2, baseOctave - 3); // One octave higher
      baseFrequency = cFreq * frequencyRatios[adjustedNote];
    }
    
    // Adjust octave for glockenspiel (higher pitched)
    if (instrumentType === 'glockenspiel') {
      baseFrequency *= 16; // Four octaves higher (2^4)
    }
    
    // Apply instrument pitch adjustments
    if (instrumentType !== 'glockenspiel') {
      baseFrequency *= 4; // Both xylophone and metallophone are 2 octaves up from notated pitch
    }
    
    return baseFrequency;
  };
  
  // Function to play a note
  const playNote = (note, octave) => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    
    // Create oscillator
    const oscillator = audioContextRef.current.createOscillator();
    const gainNode = audioContextRef.current.createGain();
    
    // Set oscillator type based on instrument
    if (instrumentType === 'xylophone') {
      oscillator.type = 'sine'; // More accurate than triangle for wooden sound
    } else {
      oscillator.type = 'sine'; // Metallophone and glockenspiel use sine wave
    }
    
    // Set frequency based on note and octave
    oscillator.frequency.value = getNoteFrequency(note, octave);
    
    // Set decay time based on instrument type
    let decayTime = 0.8; // Default for xylophone
    if (instrumentType === 'metallophone') decayTime = 2.0;
    if (instrumentType === 'glockenspiel') decayTime = 1.2;
    
    // Set envelope
    gainNode.gain.setValueAtTime(0.8, audioContextRef.current.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.001, audioContextRef.current.currentTime + decayTime);
    
    // Connect and start
    oscillator.connect(gainNode);
    gainNode.connect(audioContextRef.current.destination);
    oscillator.start();
    oscillator.stop(audioContextRef.current.currentTime + decayTime);
    
    // Set visual feedback - Add to playing notes array
    const noteId = `${note}${octave}`;
    setPlayingNotes(prev => {
      if (!prev.includes(noteId)) {
        return [...prev, noteId];
      }
      return prev;
    });
    
    // Remove visual feedback after duration
    setTimeout(() => {
      setPlayingNotes(prev => prev.filter(id => id !== noteId));
    }, 300);

    // Set recently touched to prevent double triggers
    setRecentlyTouched(prev => ({
      ...prev,
      [noteId]: true
    }));
    
    setTimeout(() => {
      setRecentlyTouched(prev => ({
        ...prev,
        [noteId]: false
      }));
    }, 50);
  };
  
  // Function to get bar height based on note and octave
  const getBarHeight = (note, octave) => {
    let baseOctave = 2;
    if (instrumentRange === 'soprano') baseOctave = 3;
    if (instrumentRange === 'bass') baseOctave = 1;
  
    const lengthRatios = {
      'C': 1.000, 'D': 0.943, 'E': 0.894, 'F': 0.866,
      'G': 0.816, 'A': 0.775, 'B': 0.730,
    };
    const secondOctaveRatios = {
      'C': 0.707, 'D': 0.667, 'E': 0.632, 'F': 0.612,
      'G': 0.577, 'A': 0.548,
    };
  
    const container = document.querySelector('.instrument-container');
    const containerHeight = container?.offsetHeight || 500;
    const containerWidth = container?.clientWidth || 800;
  
    // Estimate max bar height as the smaller of height or a multiple of bar width
    const barCount = 14; // or activeNotes.length if dynamic
    const estimatedBarWidth = containerWidth / barCount;
    const maxResponsiveHeight = Math.min(containerHeight * 0.95, estimatedBarWidth * 4);
  
    const baseHeight = maxResponsiveHeight;
  
    if (octave === baseOctave + 1) {
      return Math.round(baseHeight * secondOctaveRatios[note]);
    }
  
    return Math.round(baseHeight * lengthRatios[note]);
  };
  
  
  
  // Enhanced touch handling for multi-note support
  const handleTouchStart = (e) => {
    lastTouchTimeRef.current = Date.now();
    // Process all current touches
    const activeBarIds = [];
    let longPressTarget = null;
    
    // Process each touch point
    for (let i = 0; i < e.touches.length; i++) {
      const touch = e.touches[i];
      const element = document.elementFromPoint(touch.clientX, touch.clientY);
      
      if (element) {
        const barElement = findBarElement(element);
        
        if (barElement) {
          const note = barElement.getAttribute('data-note');
          const octave = parseInt(barElement.getAttribute('data-octave'));
          const noteId = `${note}${octave}`;
          
          // If this is the first touch, remember it for potential long press
          if (i === 0 && e.touches.length === 1) {
            longPressTarget = { note, octave };
          }
          
          // Play note immediately if not removed and not recently triggered
          if (!removedNotes.includes(noteId) && !recentlyTouched[noteId]) {
            playNote(note, octave);
            activeBarIds.push(noteId);
          }
        }
      }
    }
    
    // Only set up long press timer if we have exactly one touch
    // This prevents accidental bar removal during chord playing
    if (longPressTarget && e.touches.length === 1) {
      const timer = setTimeout(() => {
        toggleBarVisibility(longPressTarget.note, longPressTarget.octave);
      }, 800); // 800ms for long press
      
      setLongPressTimer(timer);
    }
    
    // Update touch info with all active bars
    setTouchInfo({
      isActive: activeBarIds.length > 0,
      touchedBarIds: activeBarIds
    });
    
    // Prevent default to avoid scrolling
    if (activeBarIds.length > 0) {
      e.preventDefault();
    }
  };
  
  const handleTouchMove = (e) => {
    if (!touchInfo.isActive) return;
    
    // Clear long press timer on movement
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
    
    // Track currently touched bars
    const currentTouchedBarIds = [];
    
    // Process each touch point
    for (let i = 0; i < e.touches.length; i++) {
      const touch = e.touches[i];
      const element = document.elementFromPoint(touch.clientX, touch.clientY);
      
      if (element) {
        const barElement = findBarElement(element);
        
        if (barElement) {
          const note = barElement.getAttribute('data-note');
          const octave = parseInt(barElement.getAttribute('data-octave'));
          const noteId = `${note}${octave}`;
          
          currentTouchedBarIds.push(noteId);
          
          // Only play if this is a new bar, not removed, and not recently played
          if (!touchInfo.touchedBarIds.includes(noteId) && !removedNotes.includes(noteId) && !recentlyTouched[noteId]) {
            playNote(note, octave);
          }
        }
      }
    }
    
    // Update touch info with new touched bars
    setTouchInfo({
      isActive: true,
      touchedBarIds: currentTouchedBarIds
    });
    
    // Prevent default to avoid scrolling
    e.preventDefault();
  };
  
  const handleTouchEnd = (e) => {
    // Clear long press timer
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
    
    // If there are still touches active, update the touchedBarIds
    if (e.touches.length > 0) {
      const currentTouchedBarIds = [];
      
      for (let i = 0; i < e.touches.length; i++) {
        const touch = e.touches[i];
        const element = document.elementFromPoint(touch.clientX, touch.clientY);
        
        if (element) {
          const barElement = findBarElement(element);
          
          if (barElement) {
            const note = barElement.getAttribute('data-note');
            const octave = parseInt(barElement.getAttribute('data-octave'));
            const noteId = `${note}${octave}`;
            
            currentTouchedBarIds.push(noteId);
          }
        }
      }
      
      setTouchInfo({
        isActive: true,
        touchedBarIds: currentTouchedBarIds
      });
    } else {
      // No touches left, reset touch info
      setTouchInfo({
        isActive: false,
        touchedBarIds: []
      });
    }
  };
  
  // Find bar element from any child element
  const findBarElement = (element) => {
    let current = element;
    while (current && !current.hasAttribute('data-note')) {
      current = current.parentElement;
      if (current && current.hasAttribute('data-note')) {
        return current;
      }
    }
    return current;
  };
  
  // Function to toggle bar visibility
  const toggleBarVisibility = (note, octave) => {
    const noteId = `${note}${octave}`;
    
    setRemovedNotes(prev => {
      if (prev.includes(noteId)) {
        return prev.filter(id => id !== noteId);
      } else {
        return [...prev, noteId];
      }
    });
  };
  
  // Function to apply pentatonic scale setup in different keys
  const applyPentatonicSetup = (key = 'C') => {
    const pentatonicNotes = {
      'C': ['F', 'B'], // Remove F and B for C pentatonic
      'F': ['B', 'E'], // Remove B and E for F pentatonic
      'G': ['C', 'F']  // Remove C and F for G pentatonic
    };
    
    const notesToRemove = pentatonicNotes[key] || [];
    const removedNoteIds = [];
    
    activeNotes.forEach(({ note, octave }) => {
      if (notesToRemove.includes(note)) {
        removedNoteIds.push(`${note}${octave}`);
      }
    });
    
    setRemovedNotes(removedNoteIds);
    setSetupMenuOpen(false); // Close setup menu after selection
  };
  
  // Function to apply bordun setup in different keys
  const applyBordunSetup = (key = 'C') => {
    const bordunNotes = {
      'C': ['C', 'G'], // Keep C and G for C bordun
      'F': ['F', 'C'], // Keep F and C for F bordun
      'G': ['G', 'D']  // Keep G and D for G bordun
    };
    
    const notesToKeep = bordunNotes[key] || [];
    const removedNoteIds = [];
    
    activeNotes.forEach(({ note, octave }) => {
      if (!notesToKeep.includes(note)) {
        removedNoteIds.push(`${note}${octave}`);
      }
    });
    
    setRemovedNotes(removedNoteIds);
    setSetupMenuOpen(false); // Close setup menu after selection
  };
  
  // Function to reset all bars
  const resetBars = () => {
    setRemovedNotes([]);
    setSetupMenuOpen(false); // Close setup menu after reset
  };
  
  // Function to get bar color based on note and color mode
  const getBarColor = (note, colorMode) => {
    // Handle accidentals
    let adjustedNote = note;
    if (note === 'F' && useSharps) adjustedNote = 'F#';
    if (note === 'B' && useFlats) adjustedNote = 'Bb';
    
    if (colorMode === 'default') {
      return instrumentType === 'xylophone' ? 'bg-amber-800' : 
             instrumentType === 'metallophone' ? 'bg-gray-300' : 
             'bg-gray-100'; // Glockenspiel
    } else if (colorMode === 'chroma') {
      // ChromaNotes colors with specific hex code equivalents
      const colors = {
        'C': 'bg-red-500',     // #F72d3f
        'D': 'bg-orange-400',  // #ff9928
        'E': 'bg-yellow-300',  // #ffe103
        'F': 'bg-green-500',   // #78ca33
        'F#': 'bg-emerald-500', // #10b981 - green between F and G
        'G': 'bg-teal-600',    // #009688
        'A': 'bg-indigo-900',  // #673ab7
        'Bb': 'bg-violet-700', // #7e22ce - purple between A and B
        'B': 'bg-fuchsia-600'  // #d942a1
      };
      return colors[adjustedNote] || 'bg-gray-300';
    } else if (colorMode === 'figure') {
      // Authentic FigureNotes colors
      const colors = {
        'C': 'bg-red-600',     // Red
        'D': 'bg-amber-700',   // Brown
        'E': 'bg-gray-400',    // Grey
        'F': 'bg-blue-500',    // Blue
        'F#': 'bg-blue-500',   // Use same color as F for FigureNotes
        'G': 'bg-black',       // Black
        'A': 'bg-yellow-400',  // Yellow
        'Bb': 'bg-green-500',  // Use same color as B for FigureNotes
        'B': 'bg-green-500'    // Green
      };
      return colors[adjustedNote] || 'bg-gray-300';
    }
    
    return 'bg-gray-300'; // Default fallback
  };
  
  // Function to get text color for contrast
  const getTextColor = (note, colorMode) => {
    // Handle accidentals
    let adjustedNote = note;
    if (note === 'F' && useSharps) adjustedNote = 'F#';
    if (note === 'B' && useFlats) adjustedNote = 'Bb';
    
    if (colorMode === 'default') {
      return instrumentType === 'xylophone' ? 'text-white' : 'text-black';
    } else if (colorMode === 'chroma') {
      // Dark text on light backgrounds, light text on dark backgrounds
      const darkTextNotes = ['D', 'E', 'F', 'F#'];
      return darkTextNotes.includes(adjustedNote) ? 'text-black' : 'text-white';
    } else if (colorMode === 'figure') {
      // Text colors for contrast with FigureNotes
      const darkTextNotes = ['A', 'E']; // Yellow and Grey need dark text
      return darkTextNotes.includes(adjustedNote) ? 'text-black' : 'text-white';
    }
    
    return 'text-black'; // Default fallback
  };
  
  // Wood grain effect for xylophone
  const getWoodGrainStyle = () => {
    if (instrumentType === 'xylophone') {
      return {
        backgroundImage: 'linear-gradient(90deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0) 20%, rgba(0,0,0,0.05) 40%, rgba(0,0,0,0) 60%, rgba(0,0,0,0.1) 80%)',
        backgroundSize: '100% 100%',
      };
    }
    return {};
  };

  // Metal texture for metallophone and glockenspiel
  const getMetalStyle = () => {
    if (instrumentType === 'metallophone') {
      return {
        backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.5) 0%, rgba(210,210,210,0.6) 50%, rgba(180,180,180,0.4) 100%)',
        boxShadow: 'inset 0 0 10px rgba(0,0,0,0.1)'
      };
    } else if (instrumentType === 'glockenspiel') {
      return {
        backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.8) 0%, rgba(230,230,230,0.9) 50%, rgba(200,200,200,0.7) 100%)',
        boxShadow: 'inset 0 0 5px rgba(0,0,0,0.05)'
      };
    }
    return {};
  };

  // Get appropriate bar style based on instrument
  const getBarStyle = () => {
    if (instrumentType === 'xylophone') {
      return getWoodGrainStyle();
    } else {
      return getMetalStyle();
    }
  };

  // Get keyboard shortcut label
  const getKeyboardLabel = (index) => {
    const keyMap = ['`', '1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '-', '='];
    return keyMap[index] || '';
  };
  
  return (
    <div 
      className="flex flex-col h-screen w-full bg-gray-100 p-1 md:p-2 overflow-hidden select-none"
      onContextMenu={(e) => e.preventDefault()} // Extra layer of right-click prevention
      style={{ overscrollBehavior: 'none' }} // Prevent overscroll/back navigation
    >
      {/* Controls Toolbar */}
      <div className="flex flex-wrap justify-between items-center mb-2 bg-white p-2 rounded-lg shadow-md">
        <div className="flex flex-wrap gap-2 w-full justify-between">
          <div className="flex flex-wrap gap-2">
            <select 
              value={instrumentType}
              onChange={(e) => setInstrumentType(e.target.value)}
              className="px-3 py-2 border rounded-lg shadow-sm bg-white text-gray-800 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            >
              <option value="xylophone">Xylophone</option>
              <option value="metallophone">Metallophone</option>
              <option value="glockenspiel">Glockenspiel</option>
            </select>
            
            <select 
              value={instrumentRange}
              onChange={(e) => setInstrumentRange(e.target.value)}
              className="px-3 py-2 border rounded-lg shadow-sm bg-white text-gray-800 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            >
              <option value="soprano">Soprano</option>
              <option value="alto">Alto</option>
              <option value="bass" disabled={instrumentType === 'glockenspiel'}>Bass</option>
            </select>
          </div>
          
          <div>
            <select 
              value={colorMode}
              onChange={(e) => setColorMode(e.target.value)}
              className="px-3 py-2 border rounded-lg shadow-sm bg-white text-gray-800 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            >
              <option value="default">Default Color</option>
              <option value="chroma">Chroma-Notes</option>
              <option value="figure">Figurenotes</option>
            </select>
          </div>
        </div>
      </div>
      
      {/* Instrument Container */}
      <div 
        className="relative bg-white rounded-lg shadow-md overflow-x-auto mb-3 instrument-container max-h-[60vh]"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onContextMenu={(e) => e.preventDefault()} // Prevent right-click specifically on the instrument
        style={{ overscrollBehaviorX: 'none', touchAction: 'pan-y' }} // Prevent horizontal overscroll/back navigation while allowing vertical scrolling
      >
        <div className="flex justify-center p-2 min-h-full">
          <div className="w-full flex">
            {/* Table-based approach for precise control */}
            <table className="w-full border-collapse">
              <tbody>
                <tr className="align-middle">
                  {activeNotes.map(({ note, octave }, index) => {
                    const noteId = `${note}${octave}`;
                    const isRemoved = removedNotes.includes(noteId);
                    
                    // Bar height based on note and position
                    const barHeight = getBarHeight(note, octave);
                    
                    return (
                      <td key={noteId} className="text-center align-middle p-0 w-1/14 relative">
                        <div 
                          data-note={note}
                          data-octave={octave}
                          className={`
                            mx-auto rounded-lg transition-all duration-150 cursor-pointer
                            ${isRemoved ? 'opacity-30' : getBarColor(note, colorMode)}
                            ${playingNotes.includes(noteId) ? 'brightness-125 shadow-2xl transform scale-105 ring-2 ring-yellow-300' : 'shadow-xl'}
                            hover:brightness-110 hover:shadow-md
                          `}
                          style={{
                            height: barHeight, // Previously barHeight in first publish should this get changed
                            width: '95%', // Wider bars for better touch targets
                            position: 'relative',
                            WebkitTouchCallout: 'none',
                            WebkitUserSelect: 'none',
                            KhtmlUserSelect: 'none',
                            MozUserSelect: 'none',
                            msUserSelect: 'none',
                            userSelect: 'none',
                            ...getBarStyle()
                          }}
                          onClick={(e) => {
                            // Skip click if a recent touch event occurred
                            if (Date.now() - lastTouchTimeRef.current < 500) return;
                            if (!isRemoved) {
                              playNote(note, octave);
                            }
                          }}
                          onMouseDown={(e) => {
                            // Start long-press timer for mouse
                            const timer = setTimeout(() => {
                              toggleBarVisibility(note, octave);
                            }, 800);
                            setLongPressTimer(timer);
                          }}
                          onMouseUp={() => {
                            // Clear timer on mouse up
                            if (longPressTimer) {
                              clearTimeout(longPressTimer);
                              setLongPressTimer(null);
                            }
                          }}
                          onMouseLeave={() => {
                            // Clear timer if mouse leaves the bar
                            if (longPressTimer) {
                              clearTimeout(longPressTimer);
                              setLongPressTimer(null);
                            }
                          }}
                          onContextMenu={(e) => e.preventDefault()} // Prevent right-click on each individual bar
                        >
                          {/* Resonator hole always visible */}
                          <div 
                            className={`absolute w-4 h-4 ${isRemoved ? 'bg-gray-400' : 'bg-gray-800'} rounded-full`}
                            style={{ top: '20%', left: '50%', transform: 'translateX(-50%)', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.5)' }}
                          />
                          
                          {/* Note label - visible only when bar is not removed */}
                          {!isRemoved && (
                            <div className={`absolute bottom-2 left-1/2 transform -translate-x-1/2 font-bold text-2xl w-[80%] text-center ${getTextColor(note, colorMode)}`}>
                              {note === 'B' && useFlats ? 'B♭' : 
                               note === 'F' && useSharps ? 'F♯' : note}
                            </div>
                          )}
                        
                          {/* Figurenotes Shape Outline (centered) */}
                          {!isRemoved && colorMode === 'figure' && (
                            <div className={getFigureShapeClass(octave, colorMode)}></div>
                          )}
</div>
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
      {/* Bottom Controls */}
      <div className="bg-white rounded-lg shadow-md p-3 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 50vh)', overscrollBehavior: 'contain' }} onContextMenu={(e) => e.preventDefault()}>
        <div className="flex justify-between items-center mb-2">
          {/* Setup Toggle Button */}
          <button 
            onClick={() => setSetupMenuOpen(!setupMenuOpen)}
            className="flex items-center justify-center text-sm font-medium py-2 px-4 rounded-lg transition-colors duration-150 bg-blue-500 text-white hover:bg-blue-600"
          >
            <span className="mr-2">{setupMenuOpen ? '▼' : '▲'}</span>
            Setup Options
          </button>
          
          {/* Info Button */}
          <button
            onClick={() => setInfoMenuOpen(!infoMenuOpen)}
            className="flex items-center justify-center gap-2 text-sm font-medium py-2 px-4 rounded-lg transition-colors duration-150 bg-green-500 hover:bg-green-600 text-white"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Information
          </button>
        </div>
        
        {/* Collapsible Setup Panel */}
        
{setupMenuOpen && (
  <div className="fixed inset-0 z-40 bg-black bg-opacity-40 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={() => setSetupMenuOpen(false)}>
    <div
      className="bg-white w-full sm:w-2/3 max-h-[80vh] overflow-y-auto rounded-t-2xl sm:rounded-xl shadow-lg p-4"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
        <div className="col-span-2 md:col-span-3">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Quick Setups</h3>
        </div>

        <button onClick={() => applyPentatonicSetup('C')} className="px-3 py-2 bg-blue-500 text-white text-sm rounded-lg shadow hover:bg-blue-600 transition-colors">
          C Pentatonic
        </button>
        <button onClick={() => applyPentatonicSetup('F')} className="px-3 py-2 bg-blue-500 text-white text-sm rounded-lg shadow hover:bg-blue-600 transition-colors">
          F Pentatonic
        </button>
        <button onClick={() => applyPentatonicSetup('G')} className="px-3 py-2 bg-blue-500 text-white text-sm rounded-lg shadow hover:bg-blue-600 transition-colors">
          G Pentatonic
        </button>

        <button onClick={() => applyBordunSetup('C')} className="px-3 py-2 bg-green-500 text-white text-sm rounded-lg shadow hover:bg-green-600 transition-colors">
          C Bordun
        </button>
        <button onClick={() => applyBordunSetup('F')} className="px-3 py-2 bg-green-500 text-white text-sm rounded-lg shadow hover:bg-green-600 transition-colors">
          F Bordun
        </button>
        <button onClick={() => applyBordunSetup('G')} className="px-3 py-2 bg-green-500 text-white text-sm rounded-lg shadow hover:bg-green-600 transition-colors">
          G Bordun
        </button>

        <div className="col-span-2 md:col-span-3 mt-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Accidentals</h3>
        </div>

        <button onClick={() => setUseFlats(!useFlats)} className={`px-3 py-2 text-sm rounded-lg shadow transition-colors ${useFlats ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}>
          {useFlats ? 'B♭ → B' : 'B → B♭'}
        </button>
        <button onClick={() => setUseSharps(!useSharps)} className={`px-3 py-2 text-sm rounded-lg shadow transition-colors ${useSharps ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}>
          {useSharps ? 'F♯ → F' : 'F → F♯'}
        </button>

        <button onClick={resetBars} className="px-3 py-2 bg-gray-700 text-white text-sm rounded-lg shadow hover:bg-gray-800 transition-colors">
          Reset All Bars
        </button>
      </div>
    </div>
  </div>
)}

        
        {/* Instructions - Now collapsible */}
        
{infoMenuOpen && (
  <div className="fixed inset-0 z-40 bg-black bg-opacity-40 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={() => setInfoMenuOpen(false)}>
    <div
      className="bg-white w-full sm:w-2/3 max-h-[80vh] overflow-y-auto rounded-t-2xl sm:rounded-xl shadow-lg p-4"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex justify-between items-center mb-2">
        <h3 className="font-semibold">How to Play:</h3>
        <button
          onClick={() => setInfoMenuOpen(false)}
          className="text-gray-500 hover:text-gray-700"
          aria-label="Close Instructions"
        >
          ✕
        </button>
      </div>
      <ul className="space-y-1 pl-5 list-disc mb-3 text-sm text-gray-700">
        <li>Click or tap one or more bars to play them simultaneously</li>
        <li>Use number row keys (` through =) to play notes from left to right</li>
        <li>Long press on a bar to remove/add it</li>
        <li>Drag your finger across bars for a glissando effect</li>
        <li>Use multiple fingers for chords on touch devices</li>
      </ul>
      <h4 className="font-medium text-gray-700 mt-2 mb-1">About the Digital Orff Instrumentarium:</h4>
      <p className="text-xs text-gray-600">
        This digital instrument simulates Orff instruments commonly used in elementary music education. It was developed by Jacob Beinborn, © 2025. 
      </p>
      <p className="text-xs text-gray-600">
      Version 1.0.2: Figurenotes symbols added. <a
  href="https://www.figurenotes.org"
  target="_blank"
  rel="noopener noreferrer"
  className="inline-block mt-4 text-sm text-blue-600 hover:underline hover:text-blue-800 transition">Click here to learn more about Figurenotes.</a>
      </p>
      <p className="text-xs text-gray-600">
      Questions and feedback: jacob.beinborn@gmail.com   |   <a
  href="https://www.jacobbeinborn.com"
  target="_blank"
  rel="noopener noreferrer"
  className="inline-block mt-4 text-sm text-blue-600 hover:underline hover:text-blue-800 transition"
>Visit jacobbeinborn.com for more resources</a></p>
    </div>
  </div>
)}

      </div>
    </div>
  );
};

export default DigitalInstrumentarium;